import dayjs from 'dayjs'
import { Box, Button, Card, CardContent, MenuItem, Stack, Tab, Tabs, TextField, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ProcurementPurchaseListView from './procurement/ProcurementPurchaseListView'
import ProcurementContractDetailView from './procurement/ProcurementContractDetailView'
import ProcurementPayDataListView from './procurement/ProcurementPayDataListView'
import ProcurementInvoiceListView from './procurement/ProcurementInvoiceListView'
import ProcurementGoodAllocationListView from './procurement/ProcurementGoodAllocationListView'
import ProcurementReferencesView from './procurement/ProcurementReferencesView'
import ProcurementCVBBListView from './procurement/ProcurementCVBBListView'
import ProcurementQualityPriceDialog from './procurement/ProcurementQualityPriceDialog'
import PageHeader from '../components/PageHeader'
import DateTextField from '../components/DateTextField'

export type ForcedCloseFilter = 'all' | 'yes' | 'no'

export interface ProcurementFilters {
  startDate: string
  endDate: string
  isForcedClose: ForcedCloseFilter
}

type ProcurementTabKey =
  | 'purchase'
  | 'contract-detail'
  | 'pay-data'
  | 'invoice'
  | 'good-allocation'
  | 'references'
  | 'cvbb'

interface ProcurementTabConfig {
  key: ProcurementTabKey
  label: string
  accessLabel: string
}

const FILTER_STORAGE_KEY = 'tlc:procurement:filters'

const TAB_CONFIG: ProcurementTabConfig[] = [
  { key: 'purchase', label: 'Purchase', accessLabel: 'Purchase' },
  { key: 'contract-detail', label: 'Contract Detail', accessLabel: 'Contract Detail' },
  { key: 'pay-data', label: 'Pay Data', accessLabel: 'Pay Data' },
  { key: 'invoice', label: 'Invoice', accessLabel: 'Invoice' },
  { key: 'good-allocation', label: 'Good Allocation', accessLabel: 'Good Allocation' },
  { key: 'references', label: 'References', accessLabel: 'References' },
  { key: 'cvbb', label: 'Công văn & Biên bản', accessLabel: 'Công văn & Biên bản' },
]

const DEFAULT_TAB_KEY: ProcurementTabKey = 'purchase'

function isProcurementTabKey(value: string | undefined): value is ProcurementTabKey {
  return TAB_CONFIG.some((tab) => tab.key === value)
}

function getDefaultFilters(): ProcurementFilters {
  const now = dayjs()
  const fiscalYearStart = now.month() >= 9 ? dayjs(`${now.year()}-10-01`) : dayjs(`${now.year() - 1}-10-01`)
  const fiscalYearEnd = now.month() >= 9 ? dayjs(`${now.year() + 1}-09-30`) : dayjs(`${now.year()}-09-30`)

  return {
    startDate: fiscalYearStart.format('YYYY-MM-DD'),
    endDate: fiscalYearEnd.format('YYYY-MM-DD'),
    isForcedClose: 'all',
  }
}

function readStoredFilters(): ProcurementFilters {
  if (typeof window === 'undefined') return getDefaultFilters()

  const raw = window.localStorage.getItem(FILTER_STORAGE_KEY)
  if (!raw) return getDefaultFilters()

  try {
    const parsed = JSON.parse(raw) as Partial<ProcurementFilters>
    const defaults = getDefaultFilters()
    return {
      startDate: typeof parsed.startDate === 'string' && parsed.startDate ? parsed.startDate : defaults.startDate,
      endDate: typeof parsed.endDate === 'string' && parsed.endDate ? parsed.endDate : defaults.endDate,
      isForcedClose:
        parsed.isForcedClose === 'yes' || parsed.isForcedClose === 'no' || parsed.isForcedClose === 'all'
          ? parsed.isForcedClose
          : 'all',
    }
  } catch {
    return getDefaultFilters()
  }
}

const headerButtonSx = {
  minHeight: 24,
  px: 0.75,
  py: 0.15,
  fontSize: '0.67rem',
  lineHeight: 1.1,
  textTransform: 'none' as const,
}

const headerLabelSx = {
  whiteSpace: 'nowrap',
  fontSize: '0.72rem',
  lineHeight: 1.1,
}

const compactInputSx = {
  width: { xs: 124, sm: 132 },
  '& .MuiInputBase-root': {
    minHeight: 26,
    fontSize: '0.72rem',
  },
  '& .MuiInputBase-input': {
    py: 0.25,
    px: 0.75,
  },
}

const compactSelectSx = {
  width: 104,
  '& .MuiInputBase-root': {
    minHeight: 26,
    fontSize: '0.72rem',
  },
  '& .MuiSelect-select': {
    py: 0.25,
    px: 0.75,
  },
}

export default function ProcurementPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [filters, setFilters] = useState<ProcurementFilters>(readStoredFilters)
  const [qualityPriceOpen, setQualityPriceOpen] = useState(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters))
    }
  }, [filters])

  const activeKey: ProcurementTabKey = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[1]
    return isProcurementTabKey(candidate) ? candidate : DEFAULT_TAB_KEY
  }, [location.pathname])

  useEffect(() => {
    if (!location.pathname.startsWith('/procurement')) return

    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[1]
    if (!isProcurementTabKey(candidate)) {
      navigate('/procurement/purchase', { replace: true })
    }
  }, [location.pathname, navigate])

  const activeTab = TAB_CONFIG.find((tab) => tab.key === activeKey) ?? TAB_CONFIG[0]
  const hasInvalidRange = filters.startDate > filters.endDate

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Purchase Control"
        module="Purchase"
        actions={
        <Stack
          direction="row"
          spacing={0.45}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
          sx={{ width: '100%' }}
        >
          <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="body2" fontWeight={700} sx={headerLabelSx}>
              Delivery Lastest:
            </Typography>
            <DateTextField
              size="small"
              value={filters.startDate}
              onChange={(value) => {
                if (!value) {
                  const defaults = getDefaultFilters()
                  setFilters((prev) => ({ ...prev, startDate: defaults.startDate }))
                } else {
                  setFilters((prev) => ({ ...prev, startDate: value }))
                }
              }}
              sx={compactInputSx}
              nativeInputProps={{ 'aria-label': 'Delivery Lastest start date' }}
            />
            <DateTextField
              size="small"
              value={filters.endDate}
              error={hasInvalidRange}
              onChange={(value) => {
                if (!value) {
                  const defaults = getDefaultFilters()
                  setFilters((prev) => ({ ...prev, endDate: defaults.endDate }))
                } else {
                  setFilters((prev) => ({ ...prev, endDate: value }))
                }
              }}
              sx={compactInputSx}
              nativeInputProps={{ 'aria-label': 'Delivery Lastest end date' }}
            />
          </Stack>

          <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="body2" fontWeight={700} sx={headerLabelSx}>
              Is Forced Close:
            </Typography>
            <TextField
              select
              size="small"
              value={filters.isForcedClose}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  isForcedClose: event.target.value as ForcedCloseFilter,
                }))
              }
              sx={compactSelectSx}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Stack>

          <Button
            size="small"
            variant="outlined"
            sx={headerButtonSx}
            onClick={() => setQualityPriceOpen(true)}
          >
            Quality Price (vs Basis)
          </Button>
          <Button
            size="small"
            variant="outlined"
            sx={headerButtonSx}
            disabled={hasInvalidRange}
          >
            Export (Filter)
          </Button>
          <Button
            size="small"
            variant="outlined"
            sx={headerButtonSx}
            disabled={hasInvalidRange}
          >
            Export (All)
          </Button>
        </Stack>
        }
      />

      {hasInvalidRange && (
        <Typography variant="caption" color="error" sx={{ alignSelf: 'flex-end', mb: 1 }}>
          Start date must be before or equal to end date
        </Typography>
      )}

      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeKey}
          onChange={(_, value: ProcurementTabKey) => {
            navigate(`/procurement/${value}`, { replace: false })
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 38,
            '& .MuiTabs-indicator': { height: 2 },
            '& .MuiTab-root': {
              minHeight: 38,
              py: 0.25,
              px: 1.5,
              fontSize: '0.8rem',
              fontWeight: 600,
              textTransform: 'none',
            },
          }}
        >
          {TAB_CONFIG.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} />
          ))}
        </Tabs>

        <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pt: 2 }}>
          {activeTab.key === 'purchase' ? (
            <ProcurementPurchaseListView filters={filters} />
          ) : activeTab.key === 'contract-detail' ? (
            <ProcurementContractDetailView filters={filters} />
          ) : activeTab.key === 'pay-data' ? (
            <ProcurementPayDataListView filters={filters} />
          ) : activeTab.key === 'invoice' ? (
            <ProcurementInvoiceListView filters={filters} />
          ) : activeTab.key === 'good-allocation' ? (
            <ProcurementGoodAllocationListView filters={filters} />
          ) : activeTab.key === 'references' ? (
            <ProcurementReferencesView filters={filters} />
          ) : activeTab.key === 'cvbb' ? (
            <ProcurementCVBBListView filters={filters} />
          ) : (
            <Box sx={{ py: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {activeTab.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This screen has not been migrated from Access yet. Please use the legacy Access screen for full
                workflows until this tab is implemented.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <ProcurementQualityPriceDialog open={qualityPriceOpen} onClose={() => setQualityPriceOpen(false)} />
    </Box>
  )
}
